import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { notificationsApi } from '../api/notificationsApi';
import { useAuth } from './AuthContext';

const POLL_INTERVAL_MS = 15_000;

interface NotificationsContextValue {
  unreadCount: number;
  totalCount: number;
  loadingCounts: boolean;
  refreshCounts: () => Promise<void>;
  adjustUnread: (delta: number) => void;
}

const NotificationsContext = createContext<NotificationsContextValue | null>(null);

export const NotificationsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [loadingCounts, setLoadingCounts] = useState(false);
  const countsInitializedRef = useRef(false);
  const lastUnreadRef = useRef(0);
  const lastTotalRef = useRef(0);

  const refreshCounts = useCallback(async () => {
    if (!user) {
      setUnreadCount(0);
      setTotalCount(0);
      countsInitializedRef.current = false;
      return;
    }
    setLoadingCounts(true);
    try {
      const counts = await notificationsApi.getCounts();
      const changed =
        countsInitializedRef.current &&
        (counts.unread !== lastUnreadRef.current || counts.total !== lastTotalRef.current);
      lastUnreadRef.current = counts.unread;
      lastTotalRef.current = counts.total;
      setUnreadCount(counts.unread);
      setTotalCount(counts.total);
      countsInitializedRef.current = true;
      if (changed) {
        window.dispatchEvent(new CustomEvent('ebms:notifications-changed'));
      }
    } catch {
      // ignore — header badge is best-effort
    } finally {
      setLoadingCounts(false);
    }
  }, [user]);

  const adjustUnread = useCallback((delta: number) => {
    setUnreadCount((prev) => Math.max(0, prev + delta));
  }, []);

  useEffect(() => {
    refreshCounts();
  }, [refreshCounts]);

  useEffect(() => {
    if (!user) return undefined;
    const id = window.setInterval(refreshCounts, POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [user, refreshCounts]);

  useEffect(() => {
    if (!user) return undefined;
    const onChanged = () => {
      void refreshCounts();
      window.setTimeout(() => refreshCounts(), 600);
      window.setTimeout(() => refreshCounts(), 1500);
    };
    window.addEventListener('ebms:notifications-changed', onChanged);
    return () => window.removeEventListener('ebms:notifications-changed', onChanged);
  }, [user, refreshCounts]);

  useEffect(() => {
    if (!user) return undefined;
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void refreshCounts();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [user, refreshCounts]);

  const value = useMemo(
    () => ({
      unreadCount,
      totalCount,
      loadingCounts,
      refreshCounts,
      adjustUnread,
    }),
    [unreadCount, totalCount, loadingCounts, refreshCounts, adjustUnread],
  );

  return (
    <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>
  );
};

export function useNotifications(): NotificationsContextValue {
  const ctx = useContext(NotificationsContext);
  if (!ctx) {
    throw new Error('useNotifications must be used within NotificationsProvider');
  }
  return ctx;
}
